/**
 * Date helpers. Every date the user sees is rendered in `Asia/Kolkata` (IST),
 * regardless of the user's browser timezone. Never call `new Date()` or
 * `.toLocaleString()` directly in render paths — use these helpers.
 *
 * See AGENTS.md and .cursor/rules/i18n-money-date.mdc.
 *
 * **Listings / details:** use `formatDate` (`dd MMM yyyy` style).
 * **Form inputs:** use `IstDateField` / `IstDateTimeField` (`dd/mm/yyyy`).
 */
import { addDays, addMonths, format as formatFn, getDay, isValid, parse, startOfMonth, subDays } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

export const IST_TIMEZONE = 'Asia/Kolkata';

export type DateVariant = 'date' | 'time' | 'dateTime' | 'dateShort' | 'monthYear' | 'iso';

const PATTERNS: Record<DateVariant, string> = {
  date: 'dd MMM yyyy', // 18 Apr 2026
  dateShort: 'dd/MM/yy', // 18/04/26
  time: 'hh:mm a', // 07:42 PM
  dateTime: 'dd MMM yyyy, hh:mm a',
  monthYear: 'MMM yyyy',
  iso: "yyyy-MM-dd'T'HH:mm:ssXXX",
};

export type DateInput = Date | string | number;

function toDate(value: DateInput): Date {
  if (value instanceof Date) return value;
  return new Date(value);
}

/**
 * Format a date into its IST representation using one of the standard variants.
 *
 * @example
 *   formatDate(order.createdAt)             // "18 Apr 2026, 07:42 PM" (default variant)
 *   formatDate(order.createdAt, 'date')     // "18 Apr 2026"
 *   formatDate(order.createdAt, 'time')     // "07:42 PM"
 */
export function formatDate(value: DateInput, variant: DateVariant = 'dateTime'): string {
  const zoned = toZonedTime(toDate(value), IST_TIMEZONE);
  return formatFn(zoned, PATTERNS[variant]);
}

/** `datetime-local` input pattern — interpreted in {@link IST_TIMEZONE}. */
const DATETIME_LOCAL_PATTERN = "yyyy-MM-dd'T'HH:mm";

/** Visible date-only pattern in form fields (listings keep {@link formatDate}). */
export const DATE_INPUT_DISPLAY_PATTERN = 'dd/MM/yyyy';

/** Visible datetime pattern in form fields (IST wall clock). */
export const DATETIME_INPUT_DISPLAY_PATTERN = 'dd/MM/yyyy HH:mm';

const DATE_WIRE_PATTERN = 'yyyy-MM-dd';

/**
 * API ISO datetime → value for `<input type="datetime-local" />` in IST.
 * Prefer {@link isoToDatetimeInputDisplay} / `IstDateTimeField` for UI inputs.
 */
export function isoToDatetimeLocalInput(iso: string | undefined | null): string {
  const raw = iso?.trim();
  if (!raw) return '';
  const d = toDate(raw);
  if (!isValid(d)) return '';
  const zoned = toZonedTime(d, IST_TIMEZONE);
  return formatFn(zoned, DATETIME_LOCAL_PATTERN);
}

/**
 * `datetime-local` value (IST wall clock) → ISO string for the API.
 */
export function datetimeLocalInputToIso(local: string | undefined | null): string {
  const raw = local?.trim();
  if (!raw) return '';
  const parsed = parse(raw, DATETIME_LOCAL_PATTERN, new Date());
  if (!isValid(parsed)) return '';
  return fromZonedTime(parsed, IST_TIMEZONE).toISOString();
}

/**
 * `yyyy-MM-dd` (or empty) → display string `dd/mm/yyyy` for date inputs.
 * Accepts ISO datetimes by taking the calendar date prefix.
 */
export function ymdToDateInputDisplay(ymd: string | undefined | null): string {
  const raw = ymd?.trim();
  if (!raw) return '';
  const wire =
    raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ??
    (dateInputDisplayToYmd(raw) || null);
  if (!wire) return '';
  // Pure calendar date — parse as local components to avoid UTC shift.
  const parsed = parse(wire, DATE_WIRE_PATTERN, new Date());
  if (!isValid(parsed)) return '';
  return formatFn(parsed, DATE_INPUT_DISPLAY_PATTERN);
}

/**
 * Display `dd/mm/yyyy` → wire `yyyy-MM-dd`, or `''` when empty.
 * Returns `null` when the text is non-empty but not a complete valid date.
 *
 * Also accepts wire `yyyy-MM-dd` (passthrough) so callers can re-normalize safely.
 */
export function dateInputDisplayToYmd(display: string | undefined | null): string | null {
  const raw = display?.trim();
  if (!raw) return '';

  // Wire format (optionally with time suffix)
  const wireMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (wireMatch && (raw.length === 10 || raw[10] === 'T' || raw[10] === ' ')) {
    const wire = wireMatch[1];
    const parsed = parse(wire, DATE_WIRE_PATTERN, new Date());
    if (!isValid(parsed)) return null;
    if (
      formatFn(parsed, DATE_WIRE_PATTERN) !== wire ||
      parsed.getFullYear() !== Number(wire.slice(0, 4)) ||
      parsed.getMonth() + 1 !== Number(wire.slice(5, 7)) ||
      parsed.getDate() !== Number(wire.slice(8, 10))
    ) {
      return null;
    }
    return wire;
  }

  // Accept typed variants: dd/mm/yyyy, dd-mm-yyyy, d/m/yyyy
  const normalized = raw.replace(/[-.]/g, '/');
  const parts = normalized.split('/').map((p) => p.trim());
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  if (!/^\d{1,2}$/.test(dd) || !/^\d{1,2}$/.test(mm) || !/^\d{4}$/.test(yyyy)) return null;

  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const padded = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${yyyy}`;
  const parsed = parse(padded, DATE_INPUT_DISPLAY_PATTERN, new Date());
  if (!isValid(parsed)) return null;
  // Reject overflow like 31/02/2026 rolling into March
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() + 1 !== month ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return formatFn(parsed, DATE_WIRE_PATTERN);
}

/**
 * Normalize any common date input (display or wire) to `yyyy-MM-dd`, or `''`.
 * Returns `null` for incomplete/invalid non-empty values.
 */
export function normalizeToYmd(value: string | undefined | null): string | null {
  return dateInputDisplayToYmd(value);
}

/**
 * ISO → `dd/mm/yyyy HH:mm` for datetime inputs in IST.
 */
export function isoToDatetimeInputDisplay(iso: string | undefined | null): string {
  const raw = iso?.trim();
  if (!raw) return '';
  const d = toDate(raw);
  if (!isValid(d)) {
    // Allow pre-filled datetime-local values through
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) {
      const asLocal = parse(raw.slice(0, 16), DATETIME_LOCAL_PATTERN, new Date());
      if (isValid(asLocal)) return formatFn(asLocal, DATETIME_INPUT_DISPLAY_PATTERN);
    }
    return '';
  }
  const zoned = toZonedTime(d, IST_TIMEZONE);
  return formatFn(zoned, DATETIME_INPUT_DISPLAY_PATTERN);
}

/**
 * Typed `dd/mm/yyyy HH:mm` (IST wall clock) → ISO for the API.
 * Returns `null` when non-empty but incomplete/invalid; `''` when empty.
 */
export function datetimeInputDisplayToIso(display: string | undefined | null): string | null {
  const raw = display?.trim();
  if (!raw) return '';

  // Normalize multiple spaces and loose separators
  let candidate = raw.replace(/\s+/g, ' ').replace(/[-.]/g, '/');
  // Allow "dd/mm/yyyy, HH:mm"
  candidate = candidate.replace(',', ' ').replace(/\s+/g, ' ').trim();

  const parsed = parse(candidate, DATETIME_INPUT_DISPLAY_PATTERN, new Date());
  if (!isValid(parsed)) {
    const m = candidate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const [, d, mo, y, h, mi] = m;
    const padded = `${d.padStart(2, '0')}/${mo.padStart(2, '0')}/${y} ${h.padStart(2, '0')}:${mi}`;
    const again = parse(padded, DATETIME_INPUT_DISPLAY_PATTERN, new Date());
    if (!isValid(again)) return null;
    return fromZonedTime(again, IST_TIMEZONE).toISOString();
  }
  return fromZonedTime(parsed, IST_TIMEZONE).toISOString();
}

/**
 * Digits-only → auto-insert slashes as **dd/mm/yyyy** (max 8 digits).
 * Used by {@link IstDateField} so free text cannot be entered.
 */
export function maskDdMmYyyyInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Today's calendar date in IST as `yyyy-MM-dd`. */
export function istTodayYmd(now: Date = new Date()): string {
  return formatFn(toZonedTime(now, IST_TIMEZONE), DATE_WIRE_PATTERN);
}

function parseYmdLocal(ymd: string): Date | null {
  const wire = ymd.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (!wire) return null;
  const parsed = parse(wire, DATE_WIRE_PATTERN, new Date(0));
  if (!isValid(parsed)) return null;
  return parsed;
}

/** First day of the civil month for `yyyy-MM-dd`. */
export function ymdStartOfMonth(ymd: string): string {
  const parsed = parseYmdLocal(ymd);
  if (!parsed) return ymd;
  return formatFn(startOfMonth(parsed), DATE_WIRE_PATTERN);
}

/** Shift a civil date by whole months (clamped to the month start for navigation). */
export function shiftYmdMonths(ymd: string, months: number): string {
  const start = parseYmdLocal(ymdStartOfMonth(ymd));
  if (!start) return ymd;
  return formatFn(addMonths(start, months), DATE_WIRE_PATTERN);
}

/** `yyyy-MM-dd` → `MMM yyyy` for calendar headers (civil date, no timezone shift). */
export function formatYmdMonthYear(ymd: string): string {
  const parsed = parseYmdLocal(ymd);
  if (!parsed) return ymd;
  return formatFn(parsed, 'MMM yyyy');
}

export interface CalendarDayCell {
  ymd: string;
  inMonth: boolean;
}

/**
 * 6×7 Sunday-start grid for a month (`yyyy-MM-dd` anywhere in that month).
 */
export function calendarMonthGrid(monthYmd: string): CalendarDayCell[] {
  const start = parseYmdLocal(ymdStartOfMonth(monthYmd));
  if (!start) return [];
  const gridStart = subDays(start, getDay(start));
  const month = start.getMonth();
  const cells: CalendarDayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const day = addDays(gridStart, i);
    cells.push({
      ymd: formatFn(day, DATE_WIRE_PATTERN),
      inMonth: day.getMonth() === month,
    });
  }
  return cells;
}

/**
 * Relative label for recent events ("Just now", "2 min ago", "Yesterday").
 * Falls back to `formatDate(value, 'date')` for anything older than 7 days.
 */
export function formatRelative(value: DateInput, now: DateInput = new Date()): string {
  const then = toDate(value).getTime();
  const anchor = toDate(now).getTime();
  const diffSec = Math.round((anchor - then) / 1000);

  if (diffSec < 45) return 'Just now';
  if (diffSec < 60 * 60) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 60 * 60 * 24) return `${Math.round(diffSec / 3600)} hr ago`;
  if (diffSec < 60 * 60 * 24 * 2) return 'Yesterday';
  if (diffSec < 60 * 60 * 24 * 7) return `${Math.floor(diffSec / 86400)} days ago`;
  return formatDate(value, 'date');
}
